/**
 * Post-auth user synchronisation hook (Kinde).
 *
 * After Kinde authentication succeeds the funnel needs to ensure the user
 * exists in the product backend (ndapi). The hook:
 *
 *   1. GET  `/v2/users`  — checks whether a user record already exists.
 *   2. POST `/v2/users`  — creates one when missing, using the display
 *      name collected in the funnel and a random username.
 *
 * Both requests carry `Authorization: Bearer <kindeToken>`, `x-app-id`,
 * and `x-guest-userid` (the anonymous id captured in attribution).
 *
 * The hook is intentionally side-effect-free until `sync()` is called so
 * the caller controls exactly when the network hits happen.
 */

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback, useRef, useState } from "react";

import {
  checkNdapiUserExists,
  createNdapiUser,
  safeReadAttributionContext,
  USER_SYNC_STATUS,
  type CreateArgs,
  type SyncArgs,
  type UserSyncResult,
  type UserSyncStatus,
} from "./ndapiSync";

export { USER_SYNC_STATUS };
export { responseContainsUserRecord } from "./ndapiSync";
export type { UserSyncResult, UserSyncStatus };

type KindeUser = NonNullable<ReturnType<typeof useKindeAuth>["user"]>;

const requireKindeUserForCreate = (user: KindeUser | undefined): { id: string; email: string } => {
  if (!user?.id || !user.email) throw new Error("Kinde user id/email unavailable");
  return { id: user.id, email: user.email };
};

export const usePostAuthUserSync = () => {
  const { getToken, user } = useKindeAuth();
  const [result, setResult] = useState<UserSyncResult>({ status: USER_SYNC_STATUS.IDLE });
  const syncedRef = useRef(false);
  const resultRef = useRef(result);
  resultRef.current = result;

  /**
   * Step 1: Check if user exists. Returns `"exists"` or `"needs_name"`.
   */
  const checkUser = useCallback(
    async (args: SyncArgs): Promise<UserSyncStatus> => {
      if (syncedRef.current) return resultRef.current.status;
      setResult({ status: USER_SYNC_STATUS.CHECKING });
      try {
        const token = await getToken();
        if (!token) throw new Error("No Kinde token available");
        const exists = await checkNdapiUserExists(token, args.applicationId, args.anonymousId);
        if (exists) {
          syncedRef.current = true;
          setResult({ status: USER_SYNC_STATUS.EXISTS });
          return USER_SYNC_STATUS.EXISTS;
        }
        setResult({ status: USER_SYNC_STATUS.NEEDS_NAME });
        return USER_SYNC_STATUS.NEEDS_NAME;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setResult({ status: USER_SYNC_STATUS.ERROR, error: message });
        return USER_SYNC_STATUS.ERROR;
      }
    },
    [getToken],
  );

  /**
   * Step 2: Create the user with the display name from the name step.
   */
  const createUserAccount = useCallback(
    async (args: CreateArgs): Promise<UserSyncStatus> => {
      setResult({ status: USER_SYNC_STATUS.CREATING });
      try {
        const token = await getToken();
        if (!token) throw new Error("No Kinde token available");
        const kindeUser = requireKindeUserForCreate(user);
        const attribution = safeReadAttributionContext();
        await createNdapiUser(
          token,
          args.applicationId,
          args.anonymousId,
          args.displayName,
          kindeUser,
          attribution,
        );
        syncedRef.current = true;
        setResult({ status: USER_SYNC_STATUS.CREATED });
        return USER_SYNC_STATUS.CREATED;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setResult({ status: USER_SYNC_STATUS.ERROR, error: message });
        return USER_SYNC_STATUS.ERROR;
      }
    },
    [getToken, user],
  );

  return { result, checkUser, createUserAccount };
};

export default usePostAuthUserSync;
